import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Library, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { PageLoader, PageHeader } from "@/components/common";

const TABS = [
  { key: "races", label: "Races", endpoint: "/dnd5e/races" },
  { key: "classes", label: "Classes", endpoint: "/dnd5e/classes" },
  { key: "backgrounds", label: "Backgrounds", endpoint: "/dnd5e/backgrounds" },
  { key: "feats", label: "Feats", endpoint: "/dnd5e/feats" },
  { key: "features", label: "Class Features", endpoint: "/dnd5e/features" },
];

export default function Compendium() {
  const [tab, setTab] = useState(TABS[0]);
  const [expanded, setExpanded] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["compendium", tab.key],
    queryFn: async () => (await api.get(tab.endpoint)).data,
  });

  return (
    <div>
      <PageHeader title="Compendium" subtitle="Races, classes, backgrounds, feats & features from the SRD" icon={Library} />
      <div className="flex gap-1.5 overflow-x-auto pb-2 mb-5">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => { setTab(t); setExpanded(null); }} className={`px-3.5 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition ${tab.key === t.key ? "btn-violet" : "glass glass-hover text-zinc-400"}`}>{t.label}</button>
        ))}
      </div>
      {isLoading ? <PageLoader /> : (
        <div className="grid md:grid-cols-2 gap-3">
          {data?.map((item, i) => {
            const id = item.index || item.name || i;
            const isOpen = expanded === id;
            return (
              <div key={id} className="glass rounded-2xl overflow-hidden">
                <button onClick={() => setExpanded(isOpen ? null : id)} className="w-full p-4 flex items-start gap-3 text-left">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white">{item.name}</div>
                    <div className="text-xs text-zinc-500">{renderSubtitle(tab.key, item)}</div>
                  </div>
                  <ChevronDown size={16} className={`text-zinc-500 transition ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && <DetailCard tab={tab.key} item={item} />}
              </div>
            );
          })}
          {data?.length === 0 && <p className="text-zinc-600 col-span-2 text-center py-12">Nothing here yet.</p>}
        </div>
      )}
    </div>
  );
}

function renderSubtitle(tabKey, item) {
  if (tabKey === "races") return `${item.size || "Medium"} · Speed ${item.speed || "–"} ft`;
  if (tabKey === "classes") return `Hit Die d${item.hit_die} · Saves: ${(item.saving_throws || []).join(", ")}`;
  if (tabKey === "features") return `${item.class || ""}${item.subclass ? ` (${item.subclass})` : ""} · Level ${item.level}`;
  if (tabKey === "backgrounds") return item.feature?.name || "";
  return "";
}

function DetailCard({ tab, item }) {
  return (
    <div className="px-4 pb-4 border-t border-white/[0.06] space-y-3 pt-3 text-sm">
      {tab === "races" && <RaceDetail r={item} />}
      {tab === "classes" && <ClassDetail c={item} />}
      {tab === "backgrounds" && <BackgroundDetail b={item} />}
      {tab === "feats" && <p className="text-zinc-300 whitespace-pre-line">{item.description}</p>}
      {tab === "features" && <p className="text-zinc-300 whitespace-pre-line">{item.description}</p>}
    </div>
  );
}

function Row({ label, value }) {
  if (!value || (Array.isArray(value) && !value.length)) return null;
  return (
    <div className="text-xs"><span className="text-zinc-500 uppercase tracking-wider mr-2">{label}</span><span className="text-zinc-300">{Array.isArray(value) ? value.join(", ") : value}</span></div>
  );
}

function RaceDetail({ r }) {
  return (
    <>
      <Row label="Age" value={r.age} />
      <Row label="Alignment" value={r.alignment} />
      <Row label="Size" value={r.size_description} />
      <Row label="Languages" value={r.languages} />
      <Row label="Ability Bonuses" value={(r.ability_bonuses || []).map((a) => `+${a.bonus} ${a.name}`).join(", ")} />
      <Row label="Proficiencies" value={r.starting_proficiencies} />
      <Row label="Traits" value={r.traits} />
      <Row label="Subraces" value={r.subraces} />
      <Row label="Language Note" value={r.language_desc} />
    </>
  );
}

function ClassDetail({ c }) {
  return (
    <>
      <Row label="Hit Die" value={`d${c.hit_die}`} />
      <Row label="Saving Throws" value={c.saving_throws} />
      <Row label="Proficiencies" value={c.proficiencies} />
      <Row label="Subclasses" value={c.subclasses} />
      {c.starting_equipment?.length > 0 && (
        <div className="text-xs">
          <div className="text-zinc-500 uppercase tracking-wider mb-1">Starting Equipment</div>
          <ul className="text-zinc-300 space-y-0.5">{c.starting_equipment.map((s, i) => <li key={i}>• {s.qty}× {s.name}</li>)}</ul>
        </div>
      )}
      {c.spellcasting?.spellcasting_ability && <Row label="Spellcasting" value={c.spellcasting.spellcasting_ability.name} />}
    </>
  );
}

function BackgroundDetail({ b }) {
  return (
    <>
      <Row label="Proficiencies" value={b.starting_proficiencies} />
      {b.feature && (
        <div className="text-xs">
          <div className="text-zinc-500 uppercase tracking-wider mb-1">Feature: {b.feature.name}</div>
          <p className="text-zinc-300 whitespace-pre-line">{(b.feature.desc || []).join("\n\n")}</p>
        </div>
      )}
      <Row label="Personality Traits" value={b.personality_traits} />
      <Row label="Ideals" value={(b.ideals || []).map(i => i.desc).filter(Boolean)} />
      <Row label="Bonds" value={b.bonds} />
      <Row label="Flaws" value={b.flaws} />
    </>
  );
}
